import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Row,
  Select,
  Typography
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  useTreatmentServiceCreateBirthEndpoint,
  useTreatmentServiceGetBirthEndpoint,
  useTreatmentServiceGetListPatientNewbornsEndpoint,
  useTreatmentServiceGetListPatientNewbornsEndpointKey,
  useTreatmentServiceGetNewBornEndpointKey,
  useTreatmentServiceGetPatientBirthListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  BirthType,
  FeedingType,
  Gender,
  NewbornDto,
  NewBornStatus,
  PregnancyPeriodType
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message, SaveConfirm, Text } from 'src/components'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const PersonPregnancyCreateBirthModal = ({
  id: birthId,
  onCancel,
  ...props
}: Props) => {
  const [form] = Form.useForm()

  const { t } = useLocales()

  const { personId, id } = useParams()
  const queryClient = useQueryClient()

  const { data: birthData, isLoading } = useTreatmentServiceGetBirthEndpoint({
    id: birthId ?? ''
  })

  const { data: newBorns, isLoading: isNewBornsDataLoading } =
    useTreatmentServiceGetListPatientNewbornsEndpoint(
      {
        birthIds: [String(birthId)]
      },
      [String(birthId)],
      {
        enabled: !!birthId
      }
    )

  const birthTypeOptions = Object.entries(BirthType).map(([_, value]) => ({
    value,
    label: t(`pregnancy.birth_type.${value}`)
  }))

  const pregnancyTypeOptions = Object.entries(PregnancyPeriodType).map(
    ([_, value]) => ({
      value,
      label: t(`pregnancy.period_type.${value}`)
    })
  )

  const feedingTypeOptions = Object.entries(FeedingType).map(([_, value]) => ({
    value,
    label: t(`pregnancy.feeding_types.${value}`)
  }))

  const newbornStatusOptions = Object.entries(NewBornStatus).map(
    ([_, value]) => ({
      value,
      label: t(`pregnancy.newborn_statuses.${value}`)
    })
  )

  const { mutate, isPending } = useTreatmentServiceCreateBirthEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetPatientBirthListEndpointKey]
      })
      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetNewBornEndpointKey]
      })

      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetListPatientNewbornsEndpointKey]
      })
      message.success(t('pregnancy.birth_created_successfully'))
      onCancel()
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({
      requestBody: {
        personId: String(personId),
        pregnancyPeriodType: values.pregnancyPeriodType,
        pregnancyMonitoringId: String(id),
        birthType: values.birthType,
        birthDate: dayjs(values.birthDate).format(yearMonthDaySlash),
        newborns: values.newborns?.map((newborn: NewbornDto) => ({
          firstName: newborn.firstName,
          lastName: newborn.lastName,
          feedingType: newborn.feedingType,
          status: newborn.status,
          birthDate: dayjs(newborn.birthDate).format(yearMonthDaySlash),
          gender: newborn.gender,
          currentWeightInKg: newborn.currentWeightInKg,
          currentHeightInCm: newborn.currentHeightInCm
        }))
      }
    })
  }

  useEffect(() => {
    if (birthData) {
      form.setFieldsValue({
        pregnancyPeriodType: birthData.pregnancyPeriodType,
        birthType: birthData.birthType,
        newborns: newBorns?.data
      })
    }
  }, [birthData])

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <Modal
      width={1000}
      maskClosable={false}
      open
      centered
      loading={isNewBornsDataLoading || isLoading}
      onCancel={handleCancel}
      title={t(
        birthId ? 'pregnancy.view_new_birth' : 'pregnancy.add_new_birth'
      )}
      footer={null}
      {...props}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={!!birthId}
      >
        <Flex vertical gap={16}>
          <Flex vertical gap={24}>
            <Form.Item
              label={t('pregnancy.period_type_status')}
              name="pregnancyPeriodType"
              rules={requiredRules}
            >
              <Select
                options={pregnancyTypeOptions}
                placeholder={t('pregnancy.period_type_status')}
              />
            </Form.Item>

            <Form.Item
              label={t('pregnancy.birth_type_status')}
              name="birthType"
              rules={requiredRules}
            >
              <Select
                options={birthTypeOptions}
                placeholder={t('pregnancy.birth_type_status')}
              />
            </Form.Item>

            <Form.List
              name="newborns"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(
                        new Error(t('pregnancy.at_least_one_newborn'))
                      )
                    }
                  }
                }
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <Flex vertical gap={16}>
                  <Flex justify="space-between" align="center">
                    <Text weight={600}>{t('pregnancy.newBorns')}</Text>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                    >
                      {t('pregnancy.add_newborn')}
                    </Button>
                  </Flex>
                  {fields.map(({ key, name, ...restField }) => (
                    <Flex key={key} vertical gap={8}>
                      <Flex align="flex-end">
                        <Row gutter={[16, 16]}>
                          <Col span={6}>
                            <Form.Item
                              name={[name, 'birthDate']}
                              label={t('pregnancy.birth_date')}
                              rules={requiredRules}
                              normalize={(m: Dayjs) => {
                                return dayjs(m).format(yearMonthDaySlash)
                              }}
                              getValueProps={(value) => ({
                                value: value ? dayjs(value) : undefined
                              })}
                            >
                              <DatePicker
                                format={dayMonthYearBackSlash}
                                className="full-width"
                                placeholder={t('pregnancy.birth_date')}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, 'firstName']}
                              rules={requiredRules}
                              label={t('form.first_name')}
                            >
                              <Input placeholder={t('form.first_name')} />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, 'lastName']}
                              rules={requiredRules}
                              label={t('form.last_name')}
                            >
                              <Input placeholder={t('form.last_name')} />
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, 'feedingType']}
                              rules={requiredRules}
                              label={t('pregnancy.feeding_type')}
                            >
                              <Select
                                options={feedingTypeOptions}
                                placeholder={t('pregnancy.feeding_type')}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, 'status']}
                              rules={requiredRules}
                              label={t('pregnancy.newborn_status')}
                            >
                              <Select
                                options={newbornStatusOptions}
                                placeholder={t('pregnancy.newborn_status')}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, 'gender']}
                              rules={requiredRules}
                              label={t('form.gender')}
                            >
                              <Select
                                options={[
                                  {
                                    value: Gender.MALE,
                                    label: t('form.male')
                                  },
                                  {
                                    value: Gender.FEMALE,
                                    label: t('form.female')
                                  },
                                  {
                                    value: Gender.UNKNOWN,
                                    label: t('form.unknown')
                                  }
                                ]}
                                placeholder={t('form.gender')}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, 'currentWeightInKg']}
                              rules={requiredRules}
                              label={t('pregnancy.current_weight')}
                            >
                              <InputNumber
                                min={0}
                                className="full-width"
                                step={0.01}
                                placeholder={t('pregnancy.current_weight')}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, 'currentHeightInCm']}
                              rules={requiredRules}
                              label={t('pregnancy.current_height')}
                            >
                              <InputNumber
                                className="full-width"
                                min={0}
                                step={0.01}
                                placeholder={t('pregnancy.current_height')}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          danger
                        />
                      </Flex>
                      {fields.length >= 1 && <Divider />}
                    </Flex>
                  ))}

                  {errors.length > 0 && (
                    <Typography.Text type="danger">
                      {errors.join(', ')}
                    </Typography.Text>
                  )}
                </Flex>
              )}
            </Form.List>

            {!birthId && (
              <Flex justify="center" gap={8}>
                <Button onClick={onCancel}>{t('common.cancel')}</Button>

                <SaveConfirm
                  title={`${t('common.confirm')}?`}
                  onConfirm={onSubmit}
                  okButtonProps={{ loading: isPending }}
                >
                  <Button type="primary">{t('common.confirm')}</Button>
                </SaveConfirm>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
